import type { SpanTraza } from '../api';
import { detalleDeSpan } from '../spans';

/**
 * Cascada de spans de una traza.
 *
 * Es el puente entre negocio y técnica: se llega acá desde un paso del embudo
 * y se ve, con tiempos reales, qué componente se comió la duración.
 */

interface Nodo {
  span: SpanTraza;
  hijos: Nodo[];
  profundidad: number;
}

/** ClickHouse entrega 'YYYY-MM-DD HH:MM:SS.mmm' en UTC. */
function aMilis(timestamp: string): number {
  return new Date(`${timestamp.replace(' ', 'T')}Z`).getTime();
}

function duracionMs(span: SpanTraza): number {
  return Number(span.Duration) / 1e6;
}

/**
 * Arma el árbol por ParentSpanId. Un span cuyo padre no está presente se trata
 * como raíz: con muestreo activo es normal que falten eslabones, y es preferible
 * mostrar el fragmento que existe antes que esconderlo.
 */
function construirArbol(spans: SpanTraza[]): Nodo[] {
  const porId = new Map<string, Nodo>();
  for (const span of spans) {
    porId.set(span.SpanId, { span, hijos: [], profundidad: 0 });
  }

  const raices: Nodo[] = [];
  for (const nodo of porId.values()) {
    const padre = nodo.span.ParentSpanId ? porId.get(nodo.span.ParentSpanId) : undefined;
    if (padre) padre.hijos.push(nodo);
    else raices.push(nodo);
  }

  const ordenar = (nodos: Nodo[], profundidad: number): Nodo[] => {
    nodos.sort((a, b) => aMilis(a.span.Timestamp) - aMilis(b.span.Timestamp));
    const salida: Nodo[] = [];
    for (const nodo of nodos) {
      nodo.profundidad = profundidad;
      salida.push(nodo, ...ordenar(nodo.hijos, profundidad + 1));
    }
    return salida;
  };

  return ordenar(raices, 0);
}

/**
 * Un color por servicio, para poder seguirlo de un vistazo entre los tramos.
 *
 * Sale de las variables de la hoja y no de valores escritos acá: al rediseñar
 * la paleta, estos seis se quedaron con los colores viejos porque vivían en el
 * TypeScript, donde nadie los buscó. Cuatro de ellos eran literalmente los
 * tokens anteriores.
 *
 * Ninguno es coral. En esta misma vista el coral marca los tramos con error, y
 * la paleta vieja tenía ese mismo rojo adentro: un servicio podía quedar
 * pintado como si estuviera fallando.
 */
const PALETA = [
  'var(--serie-1)',
  'var(--serie-2)',
  'var(--serie-3)',
  'var(--serie-4)',
  'var(--serie-5)',
  'var(--serie-6)',
];

function colorDe(servicio: string): string {
  let suma = 0;
  for (let i = 0; i < servicio.length; i++) suma = (suma + servicio.charCodeAt(i)) % 997;
  return PALETA[suma % PALETA.length] as string;
}

export function Cascada({ spans }: { spans: SpanTraza[] }) {
  if (spans.length === 0) {
    return <p className="vacio">Esta traza no tiene spans almacenados (pudo caer en el muestreo).</p>;
  }

  const nodos = construirArbol(spans);
  const inicio = Math.min(...spans.map((s) => aMilis(s.Timestamp)));
  const fin = Math.max(...spans.map((s) => aMilis(s.Timestamp) + duracionMs(s)));
  const total = Math.max(fin - inicio, 1);

  return (
    <div className="cascada">
      <div className="cascada-escala">
        <span>0 ms</span>
        <span>{Math.round(total)} ms</span>
      </div>

      {nodos.map(({ span, profundidad }) => {
        const desplazamiento = ((aMilis(span.Timestamp) - inicio) / total) * 100;
        const ancho = Math.max((duracionMs(span) / total) * 100, 0.6);
        const conError = span.StatusCode === 'Error';
        const esNegocio = Boolean(span.SpanAttributes['vox.kind']);
        // El mismo detalle en el nombre y en la barra: el nombre es donde se
        // pasa el mouse cuando está cortado, la barra es donde se pasa cuando
        // se mira la línea de tiempo.
        const detalle = detalleDeSpan(span);

        return (
          <div className="fila-span" key={span.SpanId}>
            <div className="span-nombre" style={{ paddingLeft: `${profundidad * 14}px` }}>
              {esNegocio && <span className="chip-negocio">negocio</span>}
              <span className="span-titulo" title={detalle}>{span.SpanName}</span>
              <span className="span-servicio">{span.ServiceName}</span>
            </div>

            <div className="span-pista">
              <div
                className={`span-barra ${conError ? 'error' : ''}`}
                style={{
                  marginLeft: `${desplazamiento}%`,
                  width: `${ancho}%`,
                  background: conError ? undefined : colorDe(span.ServiceName),
                }}
                title={detalle}
              />
              <span className="span-duracion">{Math.round(duracionMs(span))} ms</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
